import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Backend} from '@generated-backend-types';
import {UserSessionService} from '../../services/user-session.service';
import {AuthenticatedBaseComponent} from '../authenticated-base.component';
import {TitleService} from '../../services/title.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../services/content-header.service';
import {FormControlsFor, StrictFormGroup} from '@browsepedia/ng-forms';
import {FormBuilder, FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {AuthenticationService} from '../../services/api/authentication-service';
import {ToastrService} from 'ngx-toastr';
import {FormGroupComponent} from '../../components/form-inputs/form-group/form-group.component';
import {ReadOnlyStaticComponent} from '../../components/form-inputs/read-only-static/read-only-static.component';
import {TIME_MODE} from '../../components/form-inputs/date-time-selector/date-time-selector.component';
import {normalizeTimeInput, TIME_PATTERN} from '../../utils/time';
import {FormErrorService} from '../../services/form-error.service';
import AuthenticatedUser = Backend.App.Resources.Dashboard.Auth.AuthenticatedUser;
import UserData = Backend.App.Data.Dashboard.Auth.UserData;

@Component({
  selector: 'app-authenticated-account-edit',
  templateUrl: './edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslatePipe, ReactiveFormsModule, FormGroupComponent, ReadOnlyStaticComponent]
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public user?: AuthenticatedUser;

  public form: StrictFormGroup<UserData> =
    this.fb.group<FormControlsFor<UserData>>({
      email_signature_name: this.nfb.control(null, []),
      email_signature_email: this.fb.control(null, []),
      default_time_tracking_entry_start_time: this.fb.control('', Validators.pattern(TIME_PATTERN)),
      default_time_tracking_entry_end_time: this.fb.control('', Validators.pattern(TIME_PATTERN)),
    });
  protected readonly TIME_MODE = TIME_MODE;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private userSessionService: UserSessionService,
    private readonly fb: NonNullableFormBuilder,
    private readonly nfb: FormBuilder,
    private readonly authenticationService: AuthenticationService,
    private readonly toastrService: ToastrService,
    private readonly formErrorService: FormErrorService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);

    this.userSessionService.user$.subscribe(value => this.user = value ?? undefined);
  }

  public ngOnInit() {
    this.titleService.setTitle('authenticated.account.edit.title');

    this.userSessionService.user$.subscribe(user => {
        if (user !== null && user !== undefined) {
          this.form.patchValue(user, {emitEvent: true});

          if (user.time_tracking_employee) {
            this.form.patchValue(user.time_tracking_employee, {emitEvent: true});
          }
        }
      }
    );

    this.form.controls.default_time_tracking_entry_start_time.valueChanges.subscribe(val => this.formatTime(val, this.form.controls.default_time_tracking_entry_start_time));
    this.form.controls.default_time_tracking_entry_end_time.valueChanges.subscribe(val => this.formatTime(val, this.form.controls.default_time_tracking_entry_end_time));
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.markAllAsDirty();

    if (this.form.invalid) return;

    this.authenticationService.update(this.form.value as UserData).subscribe({
        next: res => {
          this.form.markAsPristine()
          this.form.markAsUntouched()
          this.toastrService.success(this.translate.instant('authenticated.account.edit.success'));

          this.userSessionService.handleLogin(res.data);

          this.cdr.markForCheck();
        },
        error: err => {
          this.toastrService.error(this.translate.instant('authenticated.account.edit.error'));
          this.formErrorService.mapErrorToForm(this.form, err)
        },
      }
    );
  }

  private formatTime(val: string | null, formControl: FormControl) {
    const normalized = normalizeTimeInput(val);

    if (normalized !== val) {
      formControl.setValue(normalized);
    }
  }
}
